import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useCurrentTeam } from "../lib/current-team.tsx";
import { useLiveSetup } from "../lib/live.ts";
import { Icon } from "./Icon.tsx";

interface Step {
  key: string;
  title: string;
  help: string;
  done: boolean;
  action?: { label: string; to: string };
}

/** The checklist from the "empty map" design. */
export function GettingStarted({ hasStuff, newStuffTo }: { hasStuff: boolean; newStuffTo: string }) {
  const { t } = useTranslation();
  const { team } = useCurrentTeam();
  // Done once the game has used this player's Live file at least once.
  const live = useLiveSetup(false);
  const steps: Step[] = [
    { key: "steam", title: t("library.stepSteam"), help: "", done: true },
    {
      key: "stuff",
      title: t("library.stepFirstStuff"),
      help: t("library.stepFirstStuffHelp"),
      done: hasStuff,
      action: { label: t("library.createFirst"), to: newStuffTo },
    },
    team
      ? {
          key: "team",
          title: t("library.stepTeam"),
          help: t("library.stepTeamInvite", { count: team.memberCount }),
          done: team.memberCount > 1,
          action: { label: t("library.stepTeamInviteAction"), to: "/team" },
        }
      : {
          key: "team",
          title: t("library.stepTeam"),
          help: t("library.stepTeamHelp"),
          done: false,
          action: { label: t("library.stepTeamAction"), to: "/setup" },
        },
    {
      key: "live",
      title: t("library.stepLive"),
      help: t("library.stepLiveHelp"),
      done: !!live.data?.lastSeenAt,
      action: { label: t("library.stepLiveAction"), to: "/live/setup" },
    },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  return (
    <aside className="card getting-started" aria-labelledby="getting-started">
      <div className="getting-started-head">
        <h2 id="getting-started" className="eyebrow">
          {t("library.gettingStarted")}
        </h2>
        <span className="muted small">{t("library.done", { done: doneCount, total: steps.length })}</span>
      </div>
      <ol className="checklist">
        {steps.map((s, i) => (
          <li key={s.key} className={s.done ? "is-done" : undefined}>
            <span className="step-disc">{s.done ? <Icon name="check" size={14} /> : i + 1}</span>
            <div className="checklist-body">
              <span className="checklist-title">
                {s.title}
                {s.done && <span className="done-tag">{t("library.doneTag")}</span>}
              </span>
              {s.help && <span className="muted small">{s.help}</span>}
              {s.action && !s.done && (
                <Link to={s.action.to} className="inline-action">
                  {s.action.label}
                  <Icon name="chevronRight" size={14} />
                </Link>
              )}
            </div>
          </li>
        ))}
      </ol>
    </aside>
  );
}
